import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchCollectionById,
  saveCollection,
  fetchCollections,
  addBookToCollection,
  removeBookFromCollection,
  updateCollectionBookPositions,
} from "@/api/collections";
import { fetchBookSummaries } from "@/api/books";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  BookMarked,
  Save,
  Plus,
  X,
  Trash,
  BookOpen,
  ImageIcon,
  MoveUp,
  MoveDown,
  GripVertical,
  Star,
  Check,
  Search,
  Eye,
  EyeOff,
  MoveVertical,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/components/ui/use-toast";
import { Collection, BookSummary } from "@/types";
import { Separator } from "@/components/ui/separator";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { cn } from "@/lib/utils";
import MediaPicker from "@/components/ui/media-picker";

// Validation schema
const formSchema = z.object({
  title: z.string().min(2, "Title must be at least 2 characters"),
  subtitle: z.string().optional(),
  coverImage: z.string().optional(),
  description: z.string().optional(),
  position: z.coerce.number().int().default(0),
  isActive: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

// Get a color for collection based on name
const getCollectionColor = (collectionTitle: string): string => {
  // Generate a consistent color based on the collection title
  const colors = [
    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
    "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
  ];

  // Create a hash of the collection title to pick a consistent color
  let hash = 0;
  for (let i = 0; i < collectionTitle.length; i++) {
    hash = collectionTitle.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);

  return colors[hash % colors.length];
};

const CollectionForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  const collectionId = id ? parseInt(id, 10) : 0;

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBooks, setSelectedBooks] = useState<BookSummary[]>([]);
  const [isSelectBooksDialogOpen, setIsSelectBooksDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch collections to find the highest position (for new collections)
  const { data: collections, isLoading: isLoadingCollections } = useQuery({
    queryKey: ["collections-listing"],
    queryFn: () =>
      fetchCollections({
        page: 1,
        pageSize: 100,
        filters: {},
      }),
    enabled: !isEditMode, // Only fetch for new collections
  });

  // Calculate the highest current position for setting a default
  const highestPosition = collections
    ? Math.max(...collections.data.map((c) => c.position ?? 0), 0)
    : 0;

  // Fetch collection for edit mode
  const { data: collection, isLoading: isLoadingCollection } = useQuery({
    queryKey: ["collection", collectionId],
    queryFn: () => fetchCollectionById(collectionId),
    enabled: isEditMode,
  });

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      subtitle: "",
      coverImage: "",
      description: "",
      position: isEditMode ? 0 : highestPosition + 1,
      isActive: true,
    },
  });

  // Populate form when collection data is available (for edit mode)
  useEffect(() => {
    if (collection && isEditMode) {
      form.reset({
        title: collection.title,
        subtitle: collection.subtitle || "",
        coverImage: collection.coverImage || "",
        description: collection.description || "",
        position: collection.position,
        isActive: collection.isActive,
      });
      setSelectedBooks(collection.books || []);
    }
  }, [collection, form, isEditMode]);

  // Update default position for new collections when collections data is loaded
  useEffect(() => {
    if (!isEditMode && collections && !form.getValues().position) {
      form.setValue("position", highestPosition + 1);
    }
  }, [collections, isEditMode, form, highestPosition]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      setIsSaving(true);
      const saveData = {
        ...data,
        id: isEditMode ? collectionId : undefined,
      };

      const result = await saveCollection(saveData);
      return result;
    },
    onSuccess: async (data) => {
      // Only try to update book positions if we have an ID and books
      if (data && data.id && selectedBooks.length > 0) {
        // First, clear existing books if needed (in edit mode)
        if (isEditMode && collection?.books) {
          // Handle reordering and new/removed books
          const saveCollectionId = data.id;

          // Get existing book IDs from the collection for comparison
          const existingBookIds = new Set(
            collection.books.map((book) => book.id)
          );

          // Add new books and reorder existing ones
          for (let i = 0; i < selectedBooks.length; i++) {
            const book = selectedBooks[i];
            await addBookToCollection(saveCollectionId, book.id, i);
          }

          // Remove books that are no longer in the selection
          const selectedBookIds = new Set(selectedBooks.map((book) => book.id));
          for (const book of collection.books) {
            if (!selectedBookIds.has(book.id)) {
              await removeBookFromCollection(saveCollectionId, book.id);
            }
          }
        } else {
          // In create mode, simply add all books with proper positions
          const saveCollectionId = data.id;
          for (let i = 0; i < selectedBooks.length; i++) {
            const book = selectedBooks[i];
            await addBookToCollection(saveCollectionId, book.id, i);
          }
        }
      }

      // Invalidate collections queries
      await queryClient.invalidateQueries({
        queryKey: ["collections-listing"],
      });
      if (isEditMode) {
        await queryClient.invalidateQueries({
          queryKey: ["collection", collectionId],
        });
      }

      // Show success toast and navigate
      toast({
        title: `Collection ${isEditMode ? "updated" : "created"} successfully`,
        description: `The collection "${
          data?.id ? form.getValues().title : ""
        }" has been ${isEditMode ? "updated" : "created"}.`,
      });

      // Navigate to collection details page
      navigate(`/admin/collections/${isEditMode ? collectionId : data.id}`);
    },
    onError: (error: Error) => {
      console.error("Error saving collection:", error);
      toast({
        title: "Error saving collection",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
      setIsSaving(false);
    },
    onSettled: () => {
      setIsSaving(false);
    },
  });

  // Books list for selection dialog
  const { data: booksData, isLoading: isLoadingBooks } = useQuery({
    queryKey: ["books-for-collection", searchTerm],
    queryFn: () =>
      fetchBookSummaries({
        page: 1,
        pageSize: 100, // Load more books for selection
        filters: {
          search: searchTerm,
        },
      }),
  });

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    saveMutation.mutate(values);
  };

  // Handler to add a book to the collection
  const handleAddBook = (book: BookSummary) => {
    // Check if the book is already selected
    if (!selectedBooks.some((selectedBook) => selectedBook.id === book.id)) {
      // Add the book with a position at the end
      const newBook = {
        ...book,
        position: selectedBooks.length,
      };
      setSelectedBooks([...selectedBooks, newBook]);
    }
  };

  // Handler to remove a book from the collection
  const handleRemoveBook = (bookId: number) => {
    const updatedBooks = selectedBooks.filter((book) => book.id !== bookId);
    // Recalculate positions
    updatedBooks.forEach((book, index) => {
      book.position = index;
    });
    setSelectedBooks(updatedBooks);
  };

  // Handler for reordering books via drag and drop
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    // Don't do anything if the position didn't change
    if (result.destination.index === result.source.index) return;

    const items = Array.from(selectedBooks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions after reordering
    const updatedItems = items.map((item, index) => ({
      ...item,
      position: index,
    }));

    // Use requestAnimationFrame to ensure DOM updates are complete
    requestAnimationFrame(() => {
      setSelectedBooks(updatedItems);
    });
  };

  // Handle book position movement
  const moveBookUp = (index: number) => {
    if (index === 0) return; // Already at the top
    const updatedBooks = [...selectedBooks];
    const temp = updatedBooks[index];
    updatedBooks[index] = updatedBooks[index - 1];
    updatedBooks[index - 1] = temp;

    // Update positions
    updatedBooks.forEach((book, i) => {
      book.position = i;
    });

    setSelectedBooks(updatedBooks);
  };

  const moveBookDown = (index: number) => {
    if (index === selectedBooks.length - 1) return; // Already at the bottom
    const updatedBooks = [...selectedBooks];
    const temp = updatedBooks[index];
    updatedBooks[index] = updatedBooks[index + 1];
    updatedBooks[index + 1] = temp;

    // Update positions
    updatedBooks.forEach((book, i) => {
      book.position = i;
    });

    setSelectedBooks(updatedBooks);
  };

  // Loading state
  if (isEditMode && isLoadingCollection) {
    return (
      <AdminLayout title={isEditMode ? "Edit Collection" : "New Collection"}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? "Edit Collection" : "New Collection"}>
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Collections
        </Button>
      </div>

      <div className="min-h-[calc(100vh-200px)]">
        <Card className="max-w-3xl mx-auto">
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <CardHeader>
              <CardTitle>
                {isEditMode ? "Edit Collection" : "Create New Collection"}
              </CardTitle>
              <CardDescription>
                {isEditMode
                  ? "Update the collection information below"
                  : "Fill out the form below to create a new collection"}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Collection Title <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="Enter collection title"
                    {...form.register("title")}
                    className={
                      form.formState.errors.title ? "border-destructive" : ""
                    }
                  />
                  {form.formState.errors.title && (
                    <p className="text-destructive text-sm">
                      {form.formState.errors.title.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Input
                    id="subtitle"
                    placeholder="Enter collection subtitle (optional)"
                    {...form.register("subtitle")}
                  />
                  <p className="text-sm text-muted-foreground">
                    A short description or tagline for this collection
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position" className="flex items-center gap-2">
                    <MoveVertical className="h-4 w-4" />
                    Display Position
                  </Label>
                  <Input
                    id="position"
                    type="number"
                    min="0"
                    placeholder={
                      isEditMode
                        ? "Current position"
                        : `Default: ${highestPosition + 1}`
                    }
                    {...form.register("position")}
                  />
                  <p className="text-sm text-muted-foreground">
                    Lower numbers appear first. Collections are displayed in
                    ascending order.
                  </p>
                  {form.formState.errors.position && (
                    <p className="text-destructive text-sm">
                      {form.formState.errors.position.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cover-image">Cover Image</Label>
                  <MediaPicker
                    onSelect={(url) => form.setValue("coverImage", url)}
                    currentValue={form.watch("coverImage")}
                    allowedTypes={["image/jpeg", "image/jpg", "image/png"]}
                    title="Select Collection Cover Image"
                    description="Choose or upload an image to use as your collection cover"
                    buttonText="Select Cover Image"
                    showPreview={true}
                  />
                  <p className="text-sm text-muted-foreground">
                    Select or upload an image for the collection's cover
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter collection description (optional)"
                    className="min-h-[120px] resize-y"
                    {...form.register("description")}
                  />
                  <p className="text-sm text-muted-foreground">
                    A detailed description of what this collection contains
                  </p>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="is-active"
                    className="flex items-center gap-2"
                  >
                    Visibility Status
                  </Label>
                  <div className="flex items-center space-x-4 border rounded-md p-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="is-active"
                        checked={form.watch("isActive")}
                        onCheckedChange={(checked) =>
                          form.setValue("isActive", checked)
                        }
                      />
                      <Label htmlFor="is-active" className="cursor-pointer">
                        {form.watch("isActive") ? "Active (Visible)" : "Hidden"}
                      </Label>
                    </div>
                    <div className="text-muted-foreground text-sm flex items-center ml-2">
                      {form.watch("isActive") ? (
                        <>
                          <Eye className="h-4 w-4 mr-2 text-green-500" />
                          <span>This collection will be visible to users</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-4 w-4 mr-2" />
                          <span>This collection will be hidden from users</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-3 border-t">
                <Label className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Books in Collection
                </Label>
                <div>
                  <Dialog
                    open={isSelectBooksDialogOpen}
                    onOpenChange={setIsSelectBooksDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button variant="outline" className="gap-2">
                        <Plus className="h-4 w-4" />
                        Add Books to Collection
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                      <DialogHeader>
                        <DialogTitle>Add Books to Collection</DialogTitle>
                        <DialogDescription>
                          Search and select books to add to this collection
                        </DialogDescription>
                      </DialogHeader>

                      <div className="my-4 flex-1 overflow-hidden flex flex-col">
                        <div className="relative mb-4">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search books by title or author..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-8"
                          />
                        </div>

                        <div className="flex-1 overflow-auto border rounded-md">
                          {isLoadingBooks ? (
                            <div className="flex justify-center my-8">
                              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                            </div>
                          ) : (
                            <Table>
                              <TableHeader className="bg-muted/50 sticky top-0 z-10">
                                <TableRow>
                                  <TableHead>Book</TableHead>
                                  <TableHead className="w-[100px]">
                                    Rating
                                  </TableHead>
                                  <TableHead className="w-[100px] text-right">
                                    Actions
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {booksData?.data &&
                                booksData.data.length > 0 ? (
                                  booksData.data.map((book) => (
                                    <TableRow
                                      key={book.id}
                                      className="hover:bg-muted/30"
                                    >
                                      <TableCell>
                                        <div className="flex items-center gap-3">
                                          {book.coverImage ? (
                                            <img
                                              src={book.coverImage}
                                              alt={book.title}
                                              className="w-10 h-10 object-cover rounded-md"
                                            />
                                          ) : (
                                            <div className="w-10 h-10 flex items-center justify-center bg-secondary rounded-md">
                                              <BookOpen className="w-5 h-5 text-secondary-foreground opacity-70" />
                                            </div>
                                          )}
                                          <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium">
                                                {book.title}
                                              </span>
                                              {book.isActive === false && (
                                                <Badge
                                                  variant="outline"
                                                  className="bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 h-5 px-1"
                                                >
                                                  <EyeOff className="h-3 w-3 mr-1" />
                                                  Unpublished
                                                </Badge>
                                              )}
                                            </div>
                                            {book.authors &&
                                              book.authors.length > 0 && (
                                                <span className="text-xs text-muted-foreground">
                                                  {book.authors
                                                    .map(
                                                      (author) => author.name
                                                    )
                                                    .join(", ")}
                                                </span>
                                              )}
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center">
                                          <Badge
                                            variant="outline"
                                            className="bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800/30 flex items-center gap-1"
                                          >
                                            <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                                            {book.rating.toFixed(1)}
                                          </Badge>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                          <Button
                                            type="button"
                                            variant={
                                              selectedBooks.some(
                                                (selectedBook) =>
                                                  selectedBook.id === book.id
                                              )
                                                ? "secondary"
                                                : "default"
                                            }
                                            size="sm"
                                            onClick={() => handleAddBook(book)}
                                            disabled={selectedBooks.some(
                                              (selectedBook) =>
                                                selectedBook.id === book.id
                                            )}
                                            className="gap-1"
                                          >
                                            {selectedBooks.some(
                                              (selectedBook) =>
                                                selectedBook.id === book.id
                                            ) ? (
                                              <>
                                                <Check className="h-3.5 w-3.5" />
                                                Added
                                              </>
                                            ) : (
                                              <>
                                                <Plus className="h-3.5 w-3.5" />
                                                Add
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell
                                      colSpan={3}
                                      className="h-24 text-center"
                                    >
                                      No books found
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </div>

                      <DialogFooter className="mt-2">
                        <div className="text-sm text-muted-foreground">
                          {selectedBooks.length} books selected
                        </div>
                        <Button
                          type="button"
                          onClick={() => setIsSelectBooksDialogOpen(false)}
                          className="gap-1"
                        >
                          <Check className="h-4 w-4" />
                          Done
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                {selectedBooks.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="books">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="w-full"
                            style={{
                              maxHeight: "400px",
                              overflowY: "auto",
                            }}
                          >
                            <Table>
                              <TableHeader className="bg-muted/50 sticky top-0 z-10">
                                <TableRow>
                                  <TableHead
                                    style={{ width: "1%" }}
                                  ></TableHead>
                                  <TableHead>Book</TableHead>
                                  <TableHead className="w-[100px]">
                                    Position
                                  </TableHead>
                                  <TableHead className="text-right w-[120px]">
                                    Actions
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {selectedBooks
                                  .sort((a, b) => a.position - b.position)
                                  .map((book, index) => (
                                    <Draggable
                                      key={book.id.toString()}
                                      draggableId={book.id.toString()}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <TableRow
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          style={{
                                            ...provided.draggableProps.style,
                                          }}
                                          key={book.id}
                                          className={`hover:bg-muted/30 ${
                                            snapshot.isDragging
                                              ? "opacity-50 bg-accent"
                                              : ""
                                          }`}
                                        >
                                          <TableCell>
                                            <div
                                              {...provided.dragHandleProps}
                                              className="cursor-grab active:cursor-grabbing flex items-center justify-center"
                                            >
                                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex items-center gap-3">
                                              {book.coverImage ? (
                                                <img
                                                  src={book.coverImage}
                                                  alt={book.title}
                                                  className="w-10 h-10 object-cover rounded-md"
                                                />
                                              ) : (
                                                <div className="w-10 h-10 flex items-center justify-center bg-secondary rounded-md">
                                                  <BookOpen className="w-5 h-5 text-secondary-foreground opacity-70" />
                                                </div>
                                              )}
                                              <div className="flex flex-col">
                                                <div className="flex items-center gap-2">
                                                  <span className="font-medium">
                                                    {book.title}
                                                  </span>
                                                  {book.isActive === false && (
                                                    <Badge
                                                      variant="outline"
                                                      className="bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 h-5 px-1"
                                                    >
                                                      <EyeOff className="h-3 w-3 mr-1" />
                                                      Unpublished
                                                    </Badge>
                                                  )}
                                                </div>
                                                {book.authors &&
                                                  book.authors.length > 0 && (
                                                    <span className="text-xs text-muted-foreground">
                                                      {book.authors
                                                        .map(
                                                          (author) =>
                                                            author.name
                                                        )
                                                        .join(", ")}
                                                    </span>
                                                  )}
                                              </div>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            <Badge
                                              variant="outline"
                                              className="bg-background"
                                            >
                                              {book.position + 1}
                                            </Badge>
                                          </TableCell>
                                          <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                  moveBookUp(index)
                                                }
                                                disabled={index === 0}
                                              >
                                                <MoveUp className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                  moveBookDown(index)
                                                }
                                                disabled={
                                                  index ===
                                                  selectedBooks.length - 1
                                                }
                                              >
                                                <MoveDown className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive hover:text-destructive"
                                                onClick={() =>
                                                  handleRemoveBook(book.id)
                                                }
                                              >
                                                <Trash className="h-4 w-4" />
                                              </Button>
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </Draggable>
                                  ))}
                              </TableBody>
                            </Table>
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                    <div className="flex items-center justify-between p-3 bg-secondary/30 text-sm">
                      <div className="text-muted-foreground">
                        {selectedBooks.length} book
                        {selectedBooks.length !== 1 ? "s" : ""} in collection
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-6 border border-dashed rounded-md">
                    <BookOpen className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground mb-2">
                      No books added to this collection yet
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Click the button above to search and add books
                    </p>
                  </div>
                )}
              </div>
            </CardContent>

            <CardFooter className="flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  navigate(
                    isEditMode
                      ? `/admin/collections/${collectionId}`
                      : "/admin/collections"
                  )
                }
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEditMode ? "Update Collection" : "Create Collection"}
                  </>
                )}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default CollectionForm;
